import type { AutorizacionSummary } from '../types/autorizacion';
import { addDaysYmd } from './date';
import { escapeHtml } from './dom';

export function fmtFechaDia(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = iso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return iso;
  const p = d.split('-');
  return (p[2] ?? '') + '/' + (p[1] ?? '') + '/' + (p[0] ?? '');
}

export interface FmtVencimientoOpts {
  withDias?: boolean;
}

export function fmtVencimiento(
  it: AutorizacionSummary,
  opts: FmtVencimientoOpts = {}
): string {
  const raw = it.fechaVencimiento;
  let iso: string | null =
    raw && /^\d{4}-\d{2}-\d{2}$/.test(String(raw).slice(0, 10))
      ? String(raw).slice(0, 10)
      : null;

  if (!iso && it.precioTipo === 'mercado' && it.fechaEmision) {
    iso = String(it.fechaEmision).slice(0, 10);
  }
  if (!iso && it.precioTipo === 'base' && it.fechaEmision && it.vencimientoDias) {
    iso = addDaysYmd(String(it.fechaEmision).slice(0, 10), Number(it.vencimientoDias));
  }
  if (iso) {
    let txt = fmtFechaDia(iso);
    if (opts.withDias && it.precioTipo === 'base' && it.vencimientoDias) {
      txt += ' (' + it.vencimientoDias + ' días)';
    }
    return txt;
  }
  if (it.vencimientoDias) return it.vencimientoDias + ' días';
  if (raw) return String(raw);
  return '—';
}

export function fmtKg(n: number | null | undefined): string {
  return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(n || 0);
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
}

export function fmtMoney(n: number | null | undefined): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(n);
}

export function fmtPct(n: number | null | undefined, digits = 0): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return (
    new Intl.NumberFormat('es-AR', {
      maximumFractionDigits: digits,
      minimumFractionDigits: digits,
    }).format(n) + ' %'
  );
}

export function fmtUsd(n: number | null | undefined): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'USD' }).format(n);
}

export function estadoBadge(estado: string | null | undefined, color: string | null | undefined): string {
  const c = color || '#f59e0b';
  return (
    '<span class="av-status" style="color:' +
    escapeHtml(c) +
    ';background-color:' +
    escapeHtml(c) +
    '1f">' +
    '<span class="av-status__dot" aria-hidden="true"></span>' +
    escapeHtml(estado || 'Pendiente') +
    '</span>'
  );
}

export function estadoBadgeCompact(
  estado: string | null | undefined,
  color: string | null | undefined,
  isFinal = false
): string {
  const c = color || '#f59e0b';
  const finalClass = isFinal ? ' av-card__status--final' : '';
  return (
    '<span class="av-card__status' +
    finalClass +
    '" style="--status-color:' +
    escapeHtml(c) +
    '">' +
    '<span class="av-card__status-dot" aria-hidden="true"></span>' +
    '<span class="av-card__status-text">' +
    escapeHtml(estado || 'Pendiente') +
    (isFinal ? ' · Final' : '') +
    '</span></span>'
  );
}
